import type { DeepKeys, DeepValue, Updater } from "@tanstack/react-form";
import type { z } from "zod";

import LabeledField from "@/components/form/labeled-field";
import type { AutosizeTextAreaProps } from "@/components/ui/autosize-textarea";
import { AutosizeTextarea } from "@/components/ui/autosize-textarea";
import type { TextareaProps } from "@/components/ui/textarea";
import { Textarea } from "@/components/ui/textarea";
import type { FieldBaseProps } from "@/types/form";
import { getOnChangeFromZod } from "@/utils/form";

export type FieldTextAreaProps<
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
  TAutosize extends boolean | undefined = false,
> = FieldBaseProps<TFormData, TName, TSchema, TData> & {
  autoSize?: TAutosize;
} & (TAutosize extends true ? AutosizeTextAreaProps : TextareaProps);

const FieldTextArea = <
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
  TAutosize extends boolean | undefined = false,
>({
  Field,
  name,
  schema,
  label,
  labelProps,
  onChange,
  validators,
  gridRow,
  autoSize,
  ...props
}: FieldTextAreaProps<TFormData, TName, TSchema, TData, TAutosize>) => {
  const TextareaComp = autoSize ? AutosizeTextarea : Textarea;
  return (
    <Field
      name={name}
      validators={{
        onChange: schema ? getOnChangeFromZod(schema, name) : undefined,
        ...(validators as any), // unsolved typing issue
      }}
      children={(field) => {
        return (
          <LabeledField
            label={label}
            fieldName={field.name.toString()}
            errors={field.state.meta.errors}
            required={props.required}
            gridRow={gridRow}
            {...labelProps}
          >
            <TextareaComp
              value={(field.state.value as string | null)?.toString() ?? ""}
              onBlur={field.handleBlur}
              onChange={(e) => {
                if (onChange) onChange(e);
                const value = e.target.value;
                (
                  field.handleChange as (
                    updater: Updater<string | null>
                  ) => void
                )(value !== "" ? value : null);
              }}
              errors={!!field.state.meta.errors.length}
              {...props}
            />
          </LabeledField>
        );
      }}
    />
  );
};

export default FieldTextArea;
